import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { 
  FileText, Calendar, IndianRupee, ArrowLeft, Clock, CheckCircle, 
  AlertCircle, XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/utils/dateFormat';

/**
 * CLIENT QUOTATIONS PAGE
 * 
 * Shows quotations published to the client with marked-up amounts.
 * Client only sees final amounts - no vendor rates or markup percentages visible.
 */
export default function ClientQuotations() {
  const [loading, setLoading] = useState(true);
  const [quotations, setQuotations] = useState([]);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchQuotations();
  }, []);

  const fetchQuotations = async () => {
    try {
      const response = await axios.get(`${API}/client/quotations`);
      setQuotations(response.data);
    } catch (error) {
      console.error('Failed to load quotations:', error);
      toast.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className="text-green-600" size={16} />;
      case 'rejected':
        return <XCircle className="text-red-600" size={16} />;
      case 'pending':
      case 'draft':
        return <Clock className="text-amber-600" size={16} />;
      default:
        return <AlertCircle className="text-stone-400" size={16} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'pending':
      case 'draft':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'partially_accepted':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      default:
        return 'bg-stone-100 text-stone-700 border-stone-200';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-stone-400">Loading quotations...</div>
        </div>
      </Layout>
    );
  }

  // Group quotations by project
  const groupedByProject = quotations.reduce((acc, quot) => {
    const projectName = quot.project_name || 'Unknown Project';
    if (!acc[projectName]) acc[projectName] = [];
    acc[projectName].push(quot);
    return acc;
  }, {});

  // Calculate totals
  const totalValue = quotations.reduce((sum, q) => sum + (q.total || 0), 0);
  const pendingCount = quotations.filter(q => q.status === 'pending' || q.status === 'draft').length;

  return (
    <Layout>
      <div className="space-y-6" data-testid="client-quotations">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft size={16} className="mr-2" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-stone-800">Your Quotations</h1>
              <p className="text-stone-500 text-sm">Review and track quotations for your projects</p>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="text-blue-600" size={20} />
              </div>
              <span className="text-2xl font-bold">{quotations.length}</span>
            </div>
            <p className="text-sm text-stone-500">Total Quotations</p>
          </div>

          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="text-amber-600" size={20} />
              </div>
              <span className="text-2xl font-bold">{pendingCount}</span>
            </div>
            <p className="text-sm text-stone-500">Pending Review</p>
          </div>

          <div className="apple-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <IndianRupee className="text-green-600" size={20} />
              </div>
              <span className="text-2xl font-bold">₹{(totalValue / 100000).toFixed(1)}L</span>
            </div>
            <p className="text-sm text-stone-500">Total Value</p>
          </div>
        </div>

        {/* Empty State */}
        {quotations.length === 0 ? (
          <div className="apple-card p-8 text-center">
            <FileText className="mx-auto mb-4 text-stone-300" size={56} />
            <h2 className="text-xl font-semibold text-stone-700 mb-2">
              No Quotations Yet
            </h2>
            <p className="text-stone-500 max-w-md mx-auto">
              Quotations will appear here once they are published for your review. 
              Check back later or contact your project manager.
            </p>
          </div>
        ) : (
          /* Quotations Grouped by Project */
          <div className="space-y-6">
            {Object.entries(groupedByProject).map(([projectName, projectQuots]) => (
              <div key={projectName} className="apple-card overflow-hidden">
                <div className="bg-stone-50 px-6 py-4 border-b">
                  <h2 className="font-semibold text-stone-800">{projectName}</h2>
                  <p className="text-sm text-stone-500">{projectQuots.length} quotation(s)</p>
                </div>

                <div className="divide-y">
                  {projectQuots.map((quot) => {
                    const isExpired = new Date(quot.valid_until) < new Date();
                    const daysRemaining = Math.ceil((new Date(quot.valid_until) - new Date()) / (1000 * 60 * 60 * 24));
                    
                    return (
                      <div 
                        key={quot.id} 
                        className="p-6 hover:bg-stone-50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-stone-800">{quot.quotation_number}</h3>
                              <Badge variant="outline" className={getStatusColor(quot.status)}>
                                {getStatusIcon(quot.status)}
                                <span className="ml-1 capitalize">{quot.status?.replace('_', ' ')}</span>
                              </Badge>
                              {isExpired && (
                                <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">
                                  Expired
                                </Badge>
                              )}
                            </div>
                            
                            <div className="flex flex-wrap gap-4 text-sm text-stone-500">
                              <span className="flex items-center gap-1">
                                <FileText size={14} />
                                {quot.items_count} items
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar size={14} />
                                Created {formatDate(new Date(quot.created_at))}
                              </span>
                              <span className={`flex items-center gap-1 ${isExpired ? 'text-red-500' : daysRemaining <= 7 ? 'text-amber-500' : ''}`}>
                                <Clock size={14} />
                                {isExpired 
                                  ? 'Expired' 
                                  : `Valid for ${daysRemaining} days`
                                }
                              </span>
                            </div>
                            
                            {quot.notes && (
                              <p className="mt-2 text-sm text-stone-600 bg-stone-50 p-2 rounded">
                                {quot.notes}
                              </p>
                            )}
                          </div>
                          
                          <div className="text-right min-w-[140px]">
                            <div className="space-y-1">
                              <p className="text-sm text-stone-500">Subtotal</p>
                              <p className="font-medium">₹{(quot.subtotal || 0).toLocaleString()}</p>
                            </div>
                            <div className="space-y-1 mt-2">
                              <p className="text-sm text-stone-500">Tax (GST)</p>
                              <p className="font-medium">₹{(quot.tax_amount || 0).toLocaleString()}</p>
                            </div>
                            <div className="border-t mt-2 pt-2">
                              <p className="text-sm text-stone-500">Total</p>
                              <p className="text-xl font-bold text-stone-800">
                                ₹{(quot.total || 0).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
